import { useId, useState, type ComponentPropsWithoutRef, type ReactNode } from "react";
import { Link } from "react-router";

/**
 * 공통 폼 컨트롤 (그리드펄스 DS 에서 가져옴).
 * 규격은 globals.css 의 control-* / field-* / btn-* / seg / chip 클래스가 정한다.
 * 호출부는 임의 style 을 넘길 수 없다 , 값,이벤트,상태,접근성 속성을 넘기고, 화면별로 정의된
 * 의미 있는 recipe class만 선택적으로 붙인다.
 * 간격,너비는 컨트롤 바깥 컨테이너가 정한다.
 */
type ControlProps<Tag extends "button" | "input" | "select" | "a" | "textarea"> = Omit<ComponentPropsWithoutRef<Tag>, "className" | "style">;

/**
 * 크기(size)와 강조 방식(variant)은 독립적이다. 선택 상태가 바뀌어도 size는 유지한다.
 * default는 폼·카드 머리·도구 모음, compact는 표·목록 안의 동작에 쓴다.
 * 같은 동작 묶음에서는 같은 size를 사용한다. 배지는 동작이 아닌 상태 표시다.
 */
const BUTTON_CLASS = {
  primary: "btn-accent",
  soft: "btn-accent-soft",
  secondary: "control-button-secondary",
  danger: "control-button-danger",
} as const;
export type ButtonVariant = keyof typeof BUTTON_CLASS;
type ButtonSize = "default" | "compact";
type ButtonAppearance = { variant?: ButtonVariant; size?: ButtonSize; fullWidth?: boolean };

function buttonClass({ variant = "secondary", size = "default", fullWidth = false }: ButtonAppearance) {
  return `control-button ${BUTTON_CLASS[variant]}${size === "compact" ? " control-button-compact" : ""}${fullWidth ? " control-full" : ""}`;
}

export function Button({ variant, size, fullWidth, type = "button", ...props }: ControlProps<"button"> & ButtonAppearance) {
  return <button {...props} type={type} className={buttonClass({ variant, size, fullWidth })} />;
}

export function ButtonLink({ variant, size, fullWidth, to, ...props }: ControlProps<"a"> & ButtonAppearance & { to?: string }) {
  const className = buttonClass({ variant, size, fullWidth });
  return to ? <Link {...props} to={to} className={className} /> : <a {...props} className={className} />;
}

/** 라벨과 체크 표시의 자리를 유지해 선택 전후 버튼의 크기가 바뀌지 않는다. */
export function ToggleButton({ selected, children, ...props }: Omit<ControlProps<"button">, "aria-pressed"> & { selected: boolean; size?: ButtonSize }) {
  return (
    <Button {...props} variant={selected ? "soft" : "secondary"} aria-pressed={selected}>
      <span className="control-toggle-mark" aria-hidden="true">✓</span>
      {children}
    </Button>
  );
}

export function Select(props: ControlProps<"select">) {
  return <select {...props} className="field-text control-input" />;
}

/** 필드 아래 오류 한 줄 , 색은 판정색(오류)이고 크기는 힌트와 같다 */
function FieldError({ error, id }: { error?: ReactNode; id: string }) {
  return error ? <p id={id} className="control-check-description text-tier-tight" role="alert">{error}</p> : null;
}

function describedBy(inputId: string, hint: ReactNode, error: ReactNode, extra?: string) {
  return [extra, hint ? `${inputId}-hint` : undefined, error ? `${inputId}-error` : undefined].filter(Boolean).join(" ") || undefined;
}

export function SelectField({ label, id, hint, error, ...props }: ControlProps<"select"> & { label: ReactNode; hint?: ReactNode; error?: ReactNode }) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  return (
    <div className="control-field">
      <label htmlFor={inputId} className="control-label">{label}</label>
      <Select {...props} id={inputId} aria-invalid={error ? true : props["aria-invalid"]} aria-describedby={describedBy(inputId, hint, error, props["aria-describedby"])} />
      {hint && <p id={`${inputId}-hint`} className="control-check-description">{hint}</p>}
      <FieldError error={error} id={`${inputId}-error`} />
    </div>
  );
}

export function TextField({ label, id, hint, error, type = "text", ...props }: ControlProps<"input"> & { label: ReactNode; hint?: ReactNode; error?: ReactNode }) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  return (
    <div className="control-field">
      <label htmlFor={inputId} className="control-label">{label}</label>
      <input {...props} id={inputId} type={type} className="field-text control-input" aria-invalid={error ? true : props["aria-invalid"]} aria-describedby={describedBy(inputId, hint, error, props["aria-describedby"])} />
      {hint && <p id={`${inputId}-hint`} className="control-check-description">{hint}</p>}
      <FieldError error={error} id={`${inputId}-error`} />
    </div>
  );
}

export function TextArea({ label, id, hint, error, ...props }: ControlProps<"textarea"> & { label: ReactNode; hint?: ReactNode; error?: ReactNode }) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  return (
    <div className="control-field">
      <label htmlFor={inputId} className="control-label">{label}</label>
      <textarea {...props} id={inputId} className="field-text control-input control-textarea" aria-invalid={error ? true : props["aria-invalid"]} aria-describedby={describedBy(inputId, hint, error, props["aria-describedby"])} />
      {hint && <p id={`${inputId}-hint`} className="control-check-description">{hint}</p>}
      <FieldError error={error} id={`${inputId}-error`} />
    </div>
  );
}

export function NumberField({ label, unit, id, hint, error, ...props }: Omit<ControlProps<"input">, "type"> & { label: ReactNode; unit: ReactNode; hint?: ReactNode; error?: ReactNode }) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  return (
    <div className="control-field">
      <label htmlFor={inputId} className="control-label">{label}</label>
      <div className="control-number-row">
        <input {...props} id={inputId} type="number" inputMode="decimal" className="control-input control-number-input" aria-invalid={error ? true : props["aria-invalid"]} aria-describedby={describedBy(inputId, hint, error, props["aria-describedby"])} />
        <span className="control-unit">{unit}</span>
      </div>
      {hint && <p id={`${inputId}-hint`} className="control-check-description">{hint}</p>}
      <FieldError error={error} id={`${inputId}-error`} />
    </div>
  );
}

export function Checkbox({ label, id, description, error, aside, indeterminate = false, ...props }: Omit<ControlProps<"input">, "type" | "children"> & { label: ReactNode; description?: ReactNode; error?: ReactNode; aside?: ReactNode; indeterminate?: boolean }) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  return (
    <div className="control-check">
      <div className="control-check-row">
        <label className="control-check-label">
          <input
            {...props}
            id={inputId}
            aria-invalid={error ? true : props["aria-invalid"]}
            aria-describedby={describedBy(inputId, description, error, props["aria-describedby"])}
            type="checkbox"
            className="control-checkbox"
            ref={(input) => {
              if (input) input.indeterminate = indeterminate;
            }}
          />
          <span>{label}</span>
        </label>
        {aside}
      </div>
      {description && <p id={`${inputId}-hint`} className="control-check-description">{description}</p>}
      <FieldError error={error} id={`${inputId}-error`} />
    </div>
  );
}

export function ChoiceButton({ selected, className = "", ...props }: Omit<ControlProps<"button">, "type" | "aria-pressed"> & { selected: boolean; className?: string }) {
  return <button {...props} type="button" aria-pressed={selected} className={`control-choice ${selected ? "chip-on" : "chip"} ${className}`.trim()} />;
}

export function SegmentedControl<Value extends string>({ label, value, options, onChange, disabled }: {
  label: string;
  value: Value;
  options: readonly { value: Value; label: string }[];
  onChange: (value: Value) => void;
  disabled?: boolean;
}) {
  return (
    <div className="seg" role="group" aria-label={label}>
      {options.map((option) => (
        <button key={option.value} type="button" disabled={disabled} aria-pressed={value === option.value} className={`seg-item${value === option.value ? " seg-item-on" : ""}`} onClick={() => onChange(option.value)}>
          {option.label}
        </button>
      ))}
    </div>
  );
}

export function FilterDisclosure({ label, summary, empty = false, defaultOpen = false, children }: { label: string; summary: string; empty?: boolean; defaultOpen?: boolean; children: ReactNode }) {
  const [open, setOpen] = useState(defaultOpen);
  const id = useId();
  return (
    <div className="control-disclosure">
      <button type="button" aria-expanded={open} aria-controls={id} onClick={() => setOpen(!open)} className="control-disclosure-trigger">
        <span className="control-disclosure-label">{label}</span>
        <span className={`control-disclosure-summary${empty ? " control-disclosure-empty" : ""}`}>{summary}</span>
        <svg viewBox="0 0 16 16" className="control-disclosure-icon" fill="none" stroke="currentColor" aria-hidden>
          <path d="m4 6 4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      <div id={id} hidden={!open} className="control-disclosure-body" role="group" aria-label={`${label} 선택`}>
        {children}
      </div>
    </div>
  );
}
